import { useEffect, useRef, useState } from 'react';
import { ayudaDePantalla, multiLocalFaqsFor, NOVEDADES_MINIATURA, type HelpFaq, type HelpScreen, type HelpVideo } from '@alexiarest/shared';
import { isDemoMode } from '../api/client';
import { ASSISTANT_DISCLAIMER, isAssistantUnavailable } from '../api/assistantStatus';
import { errorMessage } from '../api/error';
import { veSeccion, type Acceso } from '../auth/roles';
import { useAssistantChat } from '../api/hooks';
import type { AssistantMessage } from '../api/types';
import { ChatBubble, type ChatTurn } from '../components/assistant/ChatBubble';
import { ThinkingIndicator } from '../components/assistant/ThinkingIndicator';
import { Drawer } from '../components/Drawer';
import { WHATSAPP_HREF } from '../components/SupportButton';
import { BotonesTiendas } from '../components/BotonesTiendas';
import { useLocationScope } from '../context/LocationContext';
import { usePeriod } from '../context/PeriodContext';
import './ayuda.css';
import '../components/novedades/novedades.css';

/**
 * Panel de ayuda de cada pantalla. Orden decidido con Alex el 15 oct 2026
 * («Ayuda en cada pantalla»), igual en todas y visible sin bajar:
 *
 *   1. Alexia arriba: «¿Qué quieres saber?» y 2 ejemplos de esta pantalla.
 *      Contesta el «¿cómo se hace?» y el «¿qué significa este número?» con
 *      SUS datos y enlaza a la pantalla o al vídeo (ver el prompt de ayuda).
 *   2. UN vídeo de un minuto (fuera «¿Cómo funciona esta sección?»).
 *   3. Las 3 preguntas que más nos hacen de esta pantalla + «Ver todas».
 *   4. «¿No lo encuentras? Te contesta una persona · Escribir por WhatsApp»,
 *      en una línea discreta (en la demo de ventas no debe destacar).
 *
 * Salen del panel: las «¿Qué es…?» (ahora un «?» junto a la palabra, ver
 * components/QueEs.tsx), el tour, «Repetir la configuración inicial» y la
 * guía de «pantalla de inicio» (ahora «Descarga la app», solo en la Ayuda
 * general). «Con varios locales», solo con más de un local, dentro de «Ver todas».
 *
 * El contenido sale de shared/help.ts: es el mismo manual que recibe Alexia.
 */

function Flecha(): JSX.Element {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  );
}

let contador = 0;
const nuevoId = (): string => {
  contador += 1;
  return `ayuda-${contador}`;
};

/**
 * De un enlace normal (YouTube, Loom, Vimeo) al que se puede incrustar.
 * Cualquier otro se ensenya como enlace: mejor eso que un iframe en blanco.
 */
export function embedUrl(url: string): string | null {
  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\./, '');
    if (host === 'youtu.be') return `https://www.youtube-nocookie.com/embed/${u.pathname.slice(1)}`;
    if (host === 'youtube.com' || host === 'm.youtube.com' || host === 'youtube-nocookie.com') {
      const v = u.searchParams.get('v');
      if (v) return `https://www.youtube-nocookie.com/embed/${v}`;
      const m = u.pathname.match(/^\/(?:embed|shorts)\/([^/?]+)/);
      if (m) return `https://www.youtube-nocookie.com/embed/${m[1]}`;
      return null;
    }
    if (host === 'loom.com') {
      const m = u.pathname.match(/^\/(?:share|embed)\/([^/?]+)/);
      return m ? `https://www.loom.com/embed/${m[1]}` : null;
    }
    if (host === 'vimeo.com') {
      const m = u.pathname.match(/^\/(\d+)/);
      return m ? `https://player.vimeo.com/video/${m[1]}` : null;
    }
    if (host === 'player.vimeo.com') return url;
    return null;
  } catch {
    return null;
  }
}

/** Ficheros de video servidos por nosotros (bucket de ayuda): van en <video>, no en iframe. */
export function esVideoDirecto(url: string): boolean {
  return /\.(mp4|webm|m4v)(\?|#|$)/i.test(url);
}

/** Un video: el nuestro en <video>, los de YouTube/Loom/Vimeo incrustados, el resto como enlace. */
function Reproductor({ video }: { video: HelpVideo }): JSX.Element {
  if (esVideoDirecto(video.url)) {
    return (
      <div className="help__video-marco">
        {/* key: al cambiar de video el navegador descarga el nuevo, no reusa el elemento a medias */}
        {/* Sin «Descargar» (mejora 63 de Alex): se ven aqui; velocidad, pantalla
            completa y PiP siguen. El menu contextual tambien lo ofrecia. */}
        <video
          key={video.url}
          controls
          controlsList="nodownload"
          preload="metadata"
          playsInline
          src={video.url}
          onContextMenu={(e) => e.preventDefault()}
        >
          <a href={video.url}>Ver el vídeo</a>
        </video>
      </div>
    );
  }
  const src = embedUrl(video.url);
  if (src) {
    return (
      <div className="help__video-marco">
        <iframe
          src={src}
          title={video.title}
          loading="lazy"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  }
  return (
    <a href={video.url} target="_blank" rel="noopener noreferrer" className="btn btn--ghost btn--sm">
      Ver el vídeo
    </a>
  );
}

/**
 * UN vídeo de un minuto. Plegado es una fila («▶ 1 min · Cómo usar el panel
 * general»): así Alexia, el vídeo, las 3 preguntas y la persona se ven sin
 * bajar; al tocarlo se abre el reproductor ahí mismo.
 */
function VideoDeUnMinuto({ video }: { video: HelpVideo }): JSX.Element {
  const [abierto, setAbierto] = useState(false);
  if (abierto) {
    return (
      <figure className="ayuda__video">
        <Reproductor video={video} />
        <figcaption>{video.title}{video.duration ? ` · ${video.duration}` : ''}</figcaption>
      </figure>
    );
  }
  return (
    <button type="button" className="ayuda__video-fila" onClick={() => setAbierto(true)}>
      <span className="ayuda__video-play" aria-hidden="true">▶</span>
      <span className="ayuda__video-titulo">{video.title}</span>
      {video.duration && <span className="ayuda__video-dur">{video.duration}</span>}
    </button>
  );
}

/** Una pregunta plegable. */
function Pregunta({ faq }: { faq: HelpFaq }): JSX.Element {
  return (
    <details className="ayuda__faq">
      <summary>
        <span>{faq.q}</span>
        <span className="ayuda__faq-mas" aria-hidden="true">+</span>
      </summary>
      <p>{faq.a}</p>
    </details>
  );
}

export function HelpPanel({
  open,
  onClose,
  screen,
  role,
  acceso,
  pregunta,
  onNovedades,
}: {
  open: boolean;
  onClose: () => void;
  /** null = esta pantalla no tiene manual (tesoreria, admin...): ayuda general. */
  screen: HelpScreen | null;
  role?: string;
  /** Rol y permisos: deciden si esta persona tiene chat con Alexia. */
  acceso?: Acceso;
  /** «Pregúntale a Alexia» desde un «?» (15 oct 2026): se envía sola al abrir. */
  pregunta?: { texto: string; n: number } | null;
  /** Ayuda › Novedades (18 oct 2026): vuelve a abrir «Alexia tiene nueva cara». */
  onNovedades?: () => void;
}): JSX.Element | null {
  // Ni el encargado ni cocina tienen asistente (21 ago / 7 sep), y un rol
  // personalizado lo tiene si se lo han dado: vídeo y preguntas siempre.
  const conChat = veSeccion(acceso ?? { role }, 'asistente');
  // «Con varios locales» solo a quien tiene más de un local.
  const { locations } = useLocationScope();
  const variosLocales = locations.filter((l) => l.active).length > 1;
  const [todas, setTodas] = useState<string | null>(null);

  const ayuda = screen ? ayudaDePantalla(screen) : null;
  const deVariosLocales = screen && variosLocales ? multiLocalFaqsFor(screen.id) : [];
  const verTodas = todas === (screen?.id ?? 'general');
  const cuantasMas = (ayuda?.resto.length ?? 0) + deVariosLocales.length + (ayuda?.masVideos.length ?? 0);
  // «Descarga la app»: solo en la Ayuda general (sin pantalla) y en la del Panel general.
  const general = !screen || screen.id === 'panel';

  return (
    <Drawer open={open} onClose={onClose} title={screen ? `Ayuda · ${screen.title}` : 'Ayuda'} width="30rem" focusBody={false}>
      <div className="ayuda">
        {/* `key`: cambiar de pantalla remonta el chat (una respuesta en vuelo no se cuela en la nueva). */}
        {conChat && <AyudaAlexia key={screen?.id ?? 'general'} screen={screen} pregunta={pregunta} />}

        {ayuda?.video && <VideoDeUnMinuto key={`video-${screen?.id}`} video={ayuda.video} />}

        {ayuda && ayuda.destacadas.length > 0 && (
          <section className="ayuda__seccion" aria-labelledby="ayuda-preguntas">
            <h3 className="ayuda__h" id="ayuda-preguntas">Lo que más nos preguntan</h3>
            {ayuda.destacadas.map((faq) => <Pregunta key={faq.q} faq={faq} />)}
            {verTodas && ayuda.resto.map((faq) => <Pregunta key={faq.q} faq={faq} />)}
            {verTodas && deVariosLocales.length > 0 && (
              <>
                <h4 className="ayuda__h ayuda__h--sub">Con varios locales</h4>
                {deVariosLocales.map((faq) => <Pregunta key={faq.q} faq={faq} />)}
              </>
            )}
            {verTodas && ayuda.masVideos.length > 0 && (
              <>
                <h4 className="ayuda__h ayuda__h--sub">Más vídeos</h4>
                <ul className="ayuda__videos">
                  {ayuda.masVideos.map((v) => (
                    <li key={v.url}>
                      <a href={v.url} target="_blank" rel="noopener noreferrer">▶ {v.title}{v.duration ? ` · ${v.duration}` : ''}</a>
                    </li>
                  ))}
                </ul>
              </>
            )}
            {!verTodas && cuantasMas > 0 && (
              <button type="button" className="ayuda__ver-todas" onClick={() => setTodas(screen?.id ?? 'general')}>
                Ver todas ({ayuda.destacadas.length + cuantasMas})
              </button>
            )}
          </section>
        )}

        <p className="ayuda__persona">
          <span className="ayuda__persona-icono" aria-hidden="true">?</span>
          <span>
            ¿No lo encuentras? Te contesta una persona.{' '}
            <a href={WHATSAPP_HREF} target="_blank" rel="noopener noreferrer">Escribir por WhatsApp</a>
          </span>
        </p>

        {onNovedades && (
          <button type="button" className="ayuda__novedades" onClick={onNovedades}>
            <img src={NOVEDADES_MINIATURA} alt="" width={72} height={40} />
            <span>
              <strong>Novedades</strong>
              <span>Alexia tiene nueva cara: dónde quedó cada cosa</span>
            </span>
          </button>
        )}

        {general && (
          <section className="ayuda__app" aria-label="Descarga la app">
            <span className="ayuda__app-texto">
              <strong>Descarga la app</strong>
              <span>Alexia en tu teléfono, para subir facturas con una foto.</span>
            </span>
            <BotonesTiendas alto={36} />
          </section>
        )}
      </div>
    </Drawer>
  );
}

/* ------------------------------------------------------------------ */
/* 1. Alexia arriba: «¿Qué quieres saber?»                              */
/* ------------------------------------------------------------------ */

function AyudaAlexia({ screen, pregunta }: { screen: HelpScreen | null; pregunta?: { texto: string; n: number } | null }): JSX.Element {
  const { period } = usePeriod();
  const { locationId } = useLocationScope();
  const chat = useAssistantChat();
  const [turns, setTurns] = useState<ChatTurn[]>([]);
  const [draft, setDraft] = useState('');
  const [failed, setFailed] = useState(false);

  const unavailable = chat.isError && isAssistantUnavailable(chat.error);
  const ejemplos = (screen?.suggestions ?? []).slice(0, 2);

  function send(history: ChatTurn[]): void {
    setTurns(history);
    setFailed(false);
    const messages: AssistantMessage[] = history.map((t) => ({ role: t.role, content: t.content }));
    chat.mutate(
      { messages, period, screen: screen?.id, locationId },
      {
        onSuccess: (data) =>
          setTurns((prev) => [...prev, { id: nuevoId(), role: 'assistant', content: data.reply, toolCalls: data.toolCalls }]),
        onError: () => setFailed(true),
      },
    );
  }

  function ask(question: string): void {
    const text = question.trim();
    if (text === '' || chat.isPending) return;
    setDraft('');
    send([...turns, { id: nuevoId(), role: 'user', content: text }]);
  }

  /*
   * «Pregúntale a Alexia» desde un «?» (Alex, 15 oct 2026): la pregunta
   * («¿Qué es «Materia prima»?») llega escrita y se envía, como un ejemplo.
   * Cada pregunta una sola vez, aunque el panel se cierre y se vuelva a abrir.
   */
  const preguntada = useRef(0);
  useEffect(() => {
    if (!pregunta || pregunta.n === preguntada.current || unavailable) return;
    preguntada.current = pregunta.n;
    const text = pregunta.texto.trim();
    if (text === '') return;
    setDraft('');
    send([...turns, { id: nuevoId(), role: 'user', content: text }]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pregunta?.n]);

  return (
    <section className="ayuda__alexia" aria-labelledby="ayuda-alexia" aria-busy={chat.isPending}>
      <h3 className="ayuda__alexia-titulo" id="ayuda-alexia">
        <span className="ayuda__alexia-a" aria-hidden="true">a</span>
        ¿Qué quieres saber?
      </h3>
      <form
        className="ayuda__pregunta"
        onSubmit={(e) => {
          e.preventDefault();
          ask(draft);
        }}
      >
        <input
          className="input"
          aria-label="Pregúntale a Alexia"
          placeholder="Pregúntale lo que sea de esta pantalla"
          value={draft}
          disabled={unavailable}
          onChange={(e) => setDraft(e.target.value)}
        />
        <button type="submit" className="btn ayuda__enviar" aria-label="Preguntar" disabled={chat.isPending || unavailable || draft.trim() === ''}>
          ↑
        </button>
      </form>

      {turns.length === 0 ? (
        <>
          {ejemplos.length > 0 && (
            <ul className="ayuda__ejemplos" aria-label="Preguntas de ejemplo">
              {ejemplos.map((q) => (
                <li key={q}>
                  <button type="button" className="ayuda__ejemplo" disabled={chat.isPending || unavailable} onClick={() => ask(q)}>
                    <span>{q}</span>
                    <Flecha />
                  </button>
                </li>
              ))}
            </ul>
          )}
          <p className="ayuda__nota">
            {isDemoMode ? 'En la demo, Alexia contesta con respuestas de ejemplo.' : 'Sabe cómo funciona esta pantalla y conoce tus datos.'}
          </p>
        </>
      ) : (
        <div className="ayuda__log">
          {turns.map((turn) => <ChatBubble key={turn.id} turn={turn} />)}
        </div>
      )}

      {chat.isPending && <ThinkingIndicator />}

      {failed && !chat.isPending && (
        <div className="chat__error" role="alert">
          <p className="alert alert--bad">
            <span className="alert__icon" aria-hidden="true">!</span>
            <span>{errorMessage(chat.error)}</span>
          </p>
          {!unavailable && (
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => send(turns)}>
              Reintentar
            </button>
          )}
        </div>
      )}

      {turns.length > 0 && !chat.isPending && (
        <div className="ayuda__otra">
          <button type="button" className="btn-enlace" onClick={() => { setTurns([]); setFailed(false); }}>
            Otra pregunta
          </button>
          <span className="ayuda__nota">{ASSISTANT_DISCLAIMER}</span>
        </div>
      )}
    </section>
  );
}
